import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

/**
 * Each field also accepts a function of the mutation input, for callers where one
 * mutation serves many rows and the dialog must name the specific target. Plain
 * values are resolved identically, so existing callers are unaffected.
 */
type OrFn<T, TInput> = T | ((input: TInput) => T);

type ConfirmOpts<TInput = unknown> = {
  title: OrFn<string, TInput>;
  message: OrFn<string, TInput>;
  destructive?: OrFn<boolean, TInput>;
  /** Override the global confirm (e.g. in tests) */
  confirmFn?: (opts: { title: string; message: string; destructive?: boolean }) => Promise<boolean>;
};

function resolve<T, TInput>(v: OrFn<T, TInput>, input: TInput): T {
  return typeof v === 'function' ? (v as (i: TInput) => T)(input) : v;
}

/**
 * Wraps a mutation with a confirmation dialog. The mutation only runs if the user accepts.
 * On reject, the mutation throws an Error('ConfirmRejected') so isError becomes true.
 */
export function useConfirmedMutation<TData, TInput>(
  mutationFn: (input: TInput) => Promise<TData>,
  opts: ConfirmOpts<TInput> & Omit<UseMutationOptions<TData, Error, TInput>, 'mutationFn'>,
) {
  const { title, message, destructive, confirmFn, ...rest } = opts;
  return useMutation<TData, Error, TInput>({
    mutationFn: async (input: TInput) => {
      const fn = confirmFn ?? (await import('./client.js')).getGlobalConfirm();
      const ok = await fn({
        title: resolve(title, input),
        message: resolve(message, input),
        destructive: destructive === undefined ? undefined : resolve(destructive, input),
      });
      if (!ok) throw new Error('ConfirmRejected');
      return mutationFn(input);
    },
    ...rest,
  });
}
